// Calendar days and clock times as plain strings: 'YYYY-MM-DD' for days and 'HH:mm' for times, always local.
// Parses what people type into date and time boxes ("tomorrow", "fri", "16/10", "2pm") and does the day arithmetic
// the date picker needs. Pure on purpose: no browser APIs, so node's test runner can load it.

type DateLike = Date | string | number;

const DAY_MS = 86_400_000;
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const WEEKDAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
const WEEKDAY_WORDS = new Map<string, number>([
  ['mon', 0], ['monday', 0],
  ['tue', 1], ['tues', 1], ['tuesday', 1],
  ['wed', 2], ['weds', 2], ['wednesday', 2],
  ['thu', 3], ['thur', 3], ['thurs', 3], ['thursday', 3],
  ['fri', 4], ['friday', 4],
  ['sat', 5], ['saturday', 5],
  ['sun', 6], ['sunday', 6],
]);

const pad = (n: number) => String(n).padStart(2, '0');

/* ---------- values for date and time fields ---------- */

/** 'YYYY-MM-DD' for a moment, in local time. */
export function toDateInput(d: DateLike): string {
  const date = d instanceof Date ? d : new Date(d);
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

/** 'HH:mm' for a moment, in local time. */
export function toTimeInput(d: Date): string {
  return `${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/** 'YYYY-MM-DDTHH:mm' for a moment, in local time. */
export function toLocalInput(d: Date): string {
  return `${toDateInput(d)}T${toTimeInput(d)}`;
}

/** The local moment for a 'YYYY-MM-DD' day and an 'HH:mm' time, or null when either is missing or impossible. */
export function parseLocalDateTime(date: string, time: string): Date | null {
  const d = /^(\d{4})-(\d{2})-(\d{2})$/.exec(date.trim());
  const t = /^(\d{1,2}):(\d{2})$/.exec(time.trim());
  if (!d || !t) return null;
  if (Number(t[1]) > 23 || Number(t[2]) > 59) return null;
  const at = new Date(Number(d[1]), Number(d[2]) - 1, Number(d[3]), Number(t[1]), Number(t[2]), 0, 0);
  if (Number.isNaN(at.getTime()) || at.getMonth() !== Number(d[2]) - 1 || at.getDate() !== Number(d[3])) return null;
  return at;
}

/** Splits 'YYYY-MM-DDTHH:mm' into its day and time. Missing parts come back empty. */
export function splitDateTime(value: string): { date: string; time: string } {
  const [date = '', time = ''] = value.split('T');
  return { date, time: time.slice(0, 5) };
}

/** 'YYYY-MM-DDTHH:mm', or '' unless both parts are there. */
export function joinDateTime(date: string, time: string): string {
  return date && time ? `${date}T${time}` : '';
}

/* ---------- day arithmetic on 'YYYY-MM-DD' ---------- */

export interface DayParts {
  year: number;
  /** 1 to 12 */
  month: number;
  day: number;
}

export function daysInMonth(year: number, month: number): number {
  return new Date(Date.UTC(year, month, 0)).getUTCDate();
}

/** 'YYYY-MM-DD' for a year, month (1 to 12) and day, or null when that day does not exist. */
export function makeDay(year: number, month: number, day: number): string | null {
  if (!Number.isInteger(year) || !Number.isInteger(month) || !Number.isInteger(day)) return null;
  if (year < 1000 || year > 9999 || month < 1 || month > 12 || day < 1 || day > daysInMonth(year, month)) return null;
  return `${year}-${pad(month)}-${pad(day)}`;
}

/** The parts of a 'YYYY-MM-DD' day, or null when it is not one. */
export function dayParts(iso: string): DayParts | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  if (!m) return null;
  const year = Number(m[1]);
  const month = Number(m[2]);
  const day = Number(m[3]);
  return makeDay(year, month, day) ? { year, month, day } : null;
}

export function isDay(value: string): boolean {
  return dayParts(value) !== null;
}

function utc(iso: string): number {
  const p = dayParts(iso);
  if (!p) throw new Error(`Not a day: ${iso}`);
  return Date.UTC(p.year, p.month - 1, p.day);
}

function fromUtc(ms: number): string {
  const d = new Date(ms);
  return `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())}`;
}

export function addDays(iso: string, days: number): string {
  return fromUtc(utc(iso) + days * DAY_MS);
}

/** Moves by whole months, keeping the day where it exists: 31 Jan plus one month is 28 or 29 Feb. */
export function addMonths(iso: string, months: number): string {
  const p = dayParts(iso);
  if (!p) throw new Error(`Not a day: ${iso}`);
  const index = p.year * 12 + (p.month - 1) + months;
  const year = Math.floor(index / 12);
  const month = (index % 12) + 1;
  return `${year}-${pad(month)}-${pad(Math.min(p.day, daysInMonth(year, month)))}`;
}

/** Whole days from one day to another: 1 when `b` is the day after `a`. */
export function daysBetween(a: string, b: string): number {
  return Math.round((utc(b) - utc(a)) / DAY_MS);
}

/** 0 for Monday up to 6 for Sunday. */
export function weekdayIndex(iso: string): number {
  return (new Date(utc(iso)).getUTCDay() + 6) % 7;
}

/** The Monday on or before the day. */
export function startOfWeek(iso: string): string {
  return addDays(iso, -weekdayIndex(iso));
}

/** The Sunday on or after the day. */
export function endOfWeek(iso: string): string {
  return addDays(iso, 6 - weekdayIndex(iso));
}

/** The first of the day's month. */
export function startOfMonth(iso: string): string {
  return `${iso.slice(0, 7)}-01`;
}

/** Six weeks of days, Monday first, covering the month that `iso` falls in. */
export function monthGrid(iso: string): string[] {
  const first = startOfWeek(startOfMonth(iso));
  return Array.from({ length: 42 }, (_, i) => addDays(first, i));
}

/** Keeps a day inside optional bounds. */
export function clampDay(iso: string, min?: string, max?: string): string {
  if (min && iso < min) return min;
  if (max && iso > max) return max;
  return iso;
}

export function inRange(iso: string, min?: string, max?: string): boolean {
  return !(min && iso < min) && !(max && iso > max);
}

/** "October 2026" */
export function monthTitle(iso: string): string {
  const p = dayParts(iso);
  return p ? `${MONTHS[p.month - 1]} ${p.year}` : '';
}

/** "Friday 16 October 2026", the spoken name of a day in the calendar. */
export function spokenDay(iso: string): string {
  const p = dayParts(iso);
  return p ? `${WEEKDAYS[weekdayIndex(iso)]} ${p.day} ${MONTHS[p.month - 1]} ${p.year}` : '';
}

/** The local moment at the start of a 'YYYY-MM-DD' day. */
export function dayToDate(iso: string): Date {
  const p = dayParts(iso);
  if (!p) return new Date(Number.NaN);
  return new Date(p.year, p.month - 1, p.day);
}

/* ---------- reading typed dates ---------- */

export interface DateTextOptions {
  /** Today as 'YYYY-MM-DD' or a moment in it. Relative words count from here. */
  today: string | Date;
  /** Earliest allowed day. A typed day without a year that falls before it moves to next year. */
  min?: string;
  /** Latest allowed day. A typed day without a year that falls after it moves to last year. */
  max?: string;
}

function monthFromWord(word: string): number | null {
  const w = word.replace(/\.$/, '');
  if (w.length < 3) return null;
  if (w === 'sept') return 9;
  const index = MONTHS.findIndex((m) => m.toLowerCase().startsWith(w));
  return index === -1 ? null : index + 1;
}

function fullYear(text: string): number {
  return text.length === 2 ? 2000 + Number(text) : Number(text);
}

/** A day typed without a year: this year, unless the bounds say it means next year or last year. */
function dayWithoutYear(today: string, month: number, day: number, min?: string, max?: string): string | null {
  const year = Number(today.slice(0, 4));
  const iso = makeDay(year, month, day) ?? makeDay(year + 1, month, day) ?? makeDay(year - 1, month, day);
  if (!iso) return null;
  if (min && iso < min) return makeDay(Number(iso.slice(0, 4)) + 1, month, day) ?? iso;
  if (max && iso > max) return makeDay(Number(iso.slice(0, 4)) - 1, month, day) ?? iso;
  return iso;
}

function relative(today: string, amount: number, unit: string): string | null {
  if (!Number.isFinite(amount) || Math.abs(amount) > 3660) return null;
  if (/^(d|days?)$/.test(unit) || unit === '') return addDays(today, amount);
  if (/^(w|wks?|weeks?)$/.test(unit)) return addDays(today, amount * 7);
  if (/^(m|mos?|months?)$/.test(unit)) return addMonths(today, amount);
  if (/^(y|yrs?|years?)$/.test(unit)) return addMonths(today, amount * 12);
  return null;
}

/**
 * Reads a day typed by hand. Returns 'YYYY-MM-DD', '' for an empty box, or null when the text is not a day.
 *
 * Understands, day first as in the UK: "today", "tomorrow", "yesterday", "fri", "next friday", "+3", "+2w",
 * "in 3 days", "2 weeks", "3 days ago", "16/10", "16.10.26", "16-10-2026", "2026-10-16", "16 Oct", "16th October",
 * "Oct 16", "October 16 2026" and "Fri 16 Oct 2026", so every date the app displays reads back as itself.
 * Bounds only steer the year of a day typed without one; whether a day is allowed is for the caller to decide.
 */
export function parseDateText(text: string, opts: DateTextOptions): string | '' | null {
  const today = typeof opts.today === 'string' ? opts.today : toDateInput(opts.today);
  if (!isDay(today)) return null;
  const s = text
    .toLowerCase()
    .replace(/,/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/\.$/, '');
  if (s === '') return '';

  if (s === 'today' || s === 'tod' || s === 'now') return today;
  if (/^(tomorrow|tom|tmr|tmrw|tmw)$/.test(s)) return addDays(today, 1);
  if (/^(yesterday|yest|yday)$/.test(s)) return addDays(today, -1);
  if (s === 'next week') return addDays(today, 7);
  if (s === 'next month') return addMonths(today, 1);

  // "fri", "friday", "next fri", "this fri": the next such day after today.
  const wd = /^(?:next |this |on )?([a-z]+)$/.exec(s);
  if (wd && WEEKDAY_WORDS.has(wd[1]!)) {
    const target = WEEKDAY_WORDS.get(wd[1]!)!;
    const ahead = (target - weekdayIndex(today) + 7) % 7 || 7;
    return addDays(today, ahead);
  }

  // "+3", "+3d", "+2w", "-1w", "in 3 days", "3 days", "2 weeks ago".
  const plus = /^([+-]) ?(\d{1,4}) ?([a-z]*)$/.exec(s);
  if (plus) return relative(today, Number(plus[2]) * (plus[1] === '-' ? -1 : 1), plus[3]!);
  const inN = /^in (\d{1,4}) ?([a-z]+)$/.exec(s);
  if (inN) return relative(today, Number(inN[1]), inN[2]!);
  const ago = /^(\d{1,4}) ?([a-z]+)( ago)?$/.exec(s);
  if (ago && !monthFromWord(ago[2]!)) return relative(today, Number(ago[1]) * (ago[3] ? -1 : 1), ago[2]!);

  // ISO and year-first: "2026-10-16", "2026/10/16".
  const iso = /^(\d{4})[-/.](\d{1,2})[-/.](\d{1,2})$/.exec(s);
  if (iso) return makeDay(Number(iso[1]), Number(iso[2]), Number(iso[3]));

  // A leading weekday is decoration: "Fri 16 Oct", "friday, 16/10".
  const rest = s.replace(/^([a-z]+)\.? (?=\d|[a-z]{3})/, (whole, word: string) => (WEEKDAY_WORDS.has(word) ? '' : whole)).trim();

  // Day first numbers: "16/10", "16.10.26", "16-10-2026", "16 10 2026".
  const num = /^(\d{1,2})[/.\- ](\d{1,2})(?:[/.\- ](\d{4}|\d{2}))?$/.exec(rest);
  if (num) {
    const day = Number(num[1]);
    const month = Number(num[2]);
    return num[3] ? makeDay(fullYear(num[3]), month, day) : dayWithoutYear(today, month, day, opts.min, opts.max);
  }

  // Words: "16 oct", "16th october 2026", "16-oct-26", "oct 16", "october 16th 2026".
  const words = rest.replace(/(\d)(st|nd|rd|th)\b/g, '$1');
  const dm = /^(\d{1,2}) ?[-/ ]?([a-z]+\.?)(?:[-/ ](\d{4}|\d{2}))?$/.exec(words);
  const md = /^([a-z]+\.?)[-/ ](\d{1,2})(?:[-/ ](\d{4}))?$/.exec(words);
  const parts = dm ? { day: dm[1]!, month: dm[2]!, year: dm[3] } : md ? { day: md[2]!, month: md[1]!, year: md[3] } : null;
  if (parts) {
    const month = monthFromWord(parts.month);
    if (!month) return null;
    const day = Number(parts.day);
    return parts.year ? makeDay(fullYear(parts.year), month, day) : dayWithoutYear(today, month, day, opts.min, opts.max);
  }

  return null;
}

/* ---------- reading typed times ---------- */

/**
 * Reads a clock time typed by hand. Returns 'HH:mm', '' for an empty box, or null when the text is not a time.
 * Understands "14:30", "9:05", "14.30", "1430", "930", "9", "2pm", "2:30 pm", "2.30PM", "12am", "noon" and "midnight".
 */
export function parseTimeText(text: string): string | '' | null {
  const s = text.toLowerCase().replace(/\s+/g, ' ').trim();
  if (s === '') return '';
  if (s === 'noon' || s === 'midday') return '12:00';
  if (s === 'midnight') return '00:00';
  const m = /^(\d{1,2})(?:[:.h]?(\d{2}))? ?(?:([ap])\.? ?(?:m\.?)?)?$/.exec(s);
  if (!m) return null;
  let hours = Number(m[1]);
  const minutes = m[2] === undefined ? 0 : Number(m[2]);
  if (minutes > 59) return null;
  if (m[3]) {
    if (hours < 1 || hours > 12) return null;
    hours = (hours % 12) + (m[3] === 'p' ? 12 : 0);
  } else if (hours > 23) {
    return null;
  }
  return `${pad(hours)}:${pad(minutes)}`;
}

/** Minutes since midnight for 'HH:mm'. */
export function timeMinutes(time: string): number {
  const [h = '0', m = '0'] = time.split(':');
  return Number(h) * 60 + Number(m);
}

/** Every time of day `step` minutes apart, from 00:00. */
export function timeOptions(step = 15): string[] {
  const every = Math.max(1, Math.min(720, Math.round(step)));
  const out: string[] = [];
  for (let m = 0; m < 24 * 60; m += every) out.push(`${pad(Math.floor(m / 60))}:${pad(m % 60)}`);
  return out;
}

/** The index of the option closest to a time, counting round the clock. */
export function nearestTime(options: string[], time: string): number {
  if (options.length === 0) return -1;
  const target = timeMinutes(time);
  let best = 0;
  let bestGap = Infinity;
  options.forEach((option, i) => {
    const raw = Math.abs(timeMinutes(option) - target);
    const gap = Math.min(raw, 24 * 60 - raw);
    if (gap < bestGap) {
      best = i;
      bestGap = gap;
    }
  });
  return best;
}
